/** Your year in messages: the total rolls in like an odometer, then your slice of the chat. */

import { Scene, Zone, type BlobSpec } from "../../story/stage";
import { useTimeline } from "../../story/timeline";
import type { You } from "../../types";
import { count } from "../shared";

const BLOBS: BlobSpec[] = [
  { colour: "yellow", x: 1000, y: 1860, r: 370, seed: 1.2, wob: 0.16 },
  { colour: "fuchsia", x: 1000, y: 260, r: 120, seed: 2.6, wob: 0.22 },
];

const BAR = 936;
const GAP = 8;

/** "50" a day, or "0.4" a day for someone quiet. */
const perDay = (n: number) => (n >= 10 ? Math.round(n).toLocaleString("en-US") : String(n));
const share = (fraction: number) => `${(fraction * 100).toFixed(1)}%`;

/** The odometer's digit size: as big as the video's, smaller for a seven-digit year. */
function odometerSize(text: string) {
  const digits = text.replace(/,/g, "").length;
  const commas = text.length - digits;
  return Math.min(300, Math.floor(950 / (digits * 0.43 + commas * 0.2)));
}

export function Messages({ messages, total }: { messages: You["messages"]; total?: number }) {
  const text = count(messages.total);
  const size = odometerSize(text);
  // Each digit's strip spins past a few rounds of 0 to 9 before it lands, later digits longer.
  const columns = [...text].map((c, i, all) => ({ c, rounds: 2 + all.slice(0, i).filter((d) => d !== ",").length }));
  const mine = Math.max(messages.share, 0.004) * (BAR - GAP);

  const root = useTimeline((s) => {
    s.rise(".kick", 0.35, { y: 0 });
    s.tl.fromTo(".odo", { autoAlpha: 0, y: 40 }, { autoAlpha: 1, y: 0, duration: 0.5 }, 0.4);
    const strips = s.q(".dcol > div");
    strips.forEach((strip, i) => {
      const steps = Number(strip.dataset.steps);
      s.tl.fromTo(strip, { y: 0 }, { y: -steps * size, duration: 1.9 + i * 0.22, ease: "power3.out" }, 0.45);
    });
    // The digits spin fast and slow to a stop (power3.out): so do the clicks.
    const roll = 1.9 + (strips.length - 1) * 0.22;
    s.ticksAlong(0.45, roll, "power3.out", 20);
    s.sound("ding", 0.45 + roll);
    s.rise("[data-part=label]", 1.1, { y: 30 });
    s.tl.fromTo("[data-part=day]", { autoAlpha: 0, scale: 0.6, transformOrigin: "0% 50%" }, { autoAlpha: 1, scale: 1, duration: 0.6, ease: "back.out(2)" }, 2.3);
    s.sound("pop", 2.3, 1.2);
    s.rise("[data-part=share-label]", 2.9, { y: 0, sound: false });
    s.tl.fromTo(".seg", { scaleX: 0 }, { scaleX: 1, duration: 0.5, stagger: 0.12, ease: "power3.out" }, 3.0);
    s.sound("zip", 3.0, 1, { length: 0.62, pan: -0.3 });
    s.rise(".you-tag", 3.4, { y: 20, sound: false });
    s.rise("[data-part=pct]", 3.3, { y: 40, sound: false });
    s.count(s.q("[data-part=pct]")[0], messages.share * 100, 3.3, 1.0, (v) => `${v.toFixed(1)}%`);
    s.rise("[data-part=of]", 3.8, { y: 24, sound: false });
  });

  return (
    <div ref={root} className="contents">
      <Scene blobs={BLOBS}>
        <Zone at="top">
          <div className="kick">Your year in messages</div>
        </Zone>
        {/* The number and its day rate are the moment: centred in whatever height there is. */}
        <Zone at="middle">
          <div className="odo" style={{ font: `800 ${size}px/${size}px var(--wr-font-display)`, height: size, fontStretch: "75%" }}>
            <span className="sr-only">{text}</span>
            {columns.map(({ c, rounds }, i) =>
              c === "," ? (
                <div key={i} className="comma" aria-hidden>
                  ,
                </div>
              ) : (
                <div key={i} className="dcol" style={{ height: size }} aria-hidden>
                  {/* The strip ends on the real digit, so the still frame reads right. */}
                  <div data-steps={rounds * 10} style={{ transform: `translateY(${-rounds * 10 * size}px)` }}>
                    {Array.from({ length: rounds * 10 }, (_, d) => (
                      <span key={d} style={{ height: size }}>
                        {d % 10}
                      </span>
                    ))}
                    <span style={{ height: size }}>{c}</span>
                  </div>
                </div>
              ),
            )}
          </div>
          <div data-part="label" className="line" style={{ top: 330 + size + 20, fontSize: 58 }}>
            messages you sent
          </div>
          <div data-part="day" className="abs disp" style={{ left: 66, right: 72, top: 790, fontSize: 150 }}>
            that's <span style={{ color: "var(--wr-yellow)" }}>{perDay(messages.per_day)}</span> a day
          </div>
        </Zone>
        {/* Your slice of the chat reads as the card's foot, so it keeps to the bottom. */}
        <Zone at="bottom">
          <div data-part="share-label" className="kick" style={{ top: 1040 }}>
            Your share of the chat
          </div>
          <div className="share-bar">
            <div className="seg" style={{ left: 0, width: mine, background: "var(--wr-yellow)" }} />
            <div className="seg" style={{ left: mine + GAP, width: BAR - GAP - mine, background: "var(--wr-ink-55)" }} />
            <div className="you-tag" style={{ left: 4 }}>
              you ↓
            </div>
          </div>
          <div data-part="pct" className="abs disp" style={{ left: 66, top: 1290, fontSize: 250, color: "var(--wr-yellow)" }}>
            {share(messages.share)}
          </div>
          {total ? (
            <div data-part="of" className="line" style={{ top: 1530, width: 700, right: "auto" }}>
              of all {count(total)} messages in the chat
            </div>
          ) : null}
        </Zone>
      </Scene>
    </div>
  );
}
